import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { T } from "@/lib/theme";
import { ArrowUpRight } from "lucide-react";
import EMark from "@/components/EMark";

/**
 * CapabilityFieldClose — closing for the Capabilities page.
 * --------------------------------------------------------------------------
 * Visual idea: a horizontal "field" diagram that reads the Erenown model as
 * one continuous flow — Launch · Operation · Institutional · Market Access ·
 * Digital Evolution — connected by a flowing topographic curve. Distinct
 * from the Orbit seal (vertical/concentric/official) and from the Arrival
 * constellation (sky map). Premium, calm, slow.
 *
 * Motion (restrained):
 *  - the flow path draws once on enter (3.5s ease-out)
 *  - capability nodes light up sequentially after the path reaches them
 *  - faint contour curves drift very slowly underneath
 * --------------------------------------------------------------------------
 */
const STAGES = [
  { id: "launch",    name: "Launch" },
  { id: "operation", name: "Operation" },
  { id: "institut",  name: "Institutional" },
  { id: "market",    name: "Market Access" },
  { id: "digital",   name: "Digital Evolution" },
];

// Viewbox is 1400 wide so the flow stretches edge-to-edge and reads as a
// horizontal field rather than a closed shape.
const VB_W = 1400;
const VB_H = 380;

export default function CapabilityFieldClose() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    // Start lighting nodes after the curve begins drawing (~600ms),
    // each ~520ms apart so the total animation is just under the curve's 3.5s.
    let i = 0;
    const start = setTimeout(() => {
      const id = setInterval(() => {
        i += 1;
        setActive(i);
        if (i >= STAGES.length) clearInterval(id);
      }, 520);
    }, 600);
    return () => clearTimeout(start);
  }, []);

  // Compute node positions along the visual midline with a gentle wave.
  const nodes = STAGES.map((s, i) => {
    const padX = 110;
    const x = padX + (i * (VB_W - padX * 2)) / (STAGES.length - 1);
    const wave = Math.sin((i / (STAGES.length - 1)) * Math.PI) * 32;
    const y = VB_H / 2 + wave - 24;
    return { ...s, x, y };
  });

  // Build a smooth flow path through all nodes (Catmull-Rom-like via cubic
  // Beziers). The control points are simple horizontal tangents — that gives
  // an elegant, calm flow without overshoot.
  const flowPath = (() => {
    if (nodes.length === 0) return "";
    let d = `M ${nodes[0].x} ${nodes[0].y}`;
    for (let i = 0; i < nodes.length - 1; i++) {
      const a = nodes[i];
      const b = nodes[i + 1];
      const dx = (b.x - a.x) * 0.5;
      d += ` C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
    }
    return d;
  })();

  // Background contour curves — three offset copies of the flow path, very
  // faint. Each drifts slowly via the existing constellation-drift keyframe.
  const contour = (offsetY) => {
    let d = `M ${nodes[0].x} ${nodes[0].y + offsetY}`;
    for (let i = 0; i < nodes.length - 1; i++) {
      const a = nodes[i];
      const b = nodes[i + 1];
      const dx = (b.x - a.x) * 0.5;
      d += ` C ${a.x + dx} ${a.y + offsetY}, ${b.x - dx} ${b.y + offsetY}, ${b.x} ${b.y + offsetY}`;
    }
    return d;
  };

  return (
    <section
      data-testid="capability-field-close"
      className="relative overflow-hidden border-t"
      style={{ borderColor: T.border, background: T.bg }}
    >
      {/* Soft warm + cool radial wash (no orbit, no constellation) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            `radial-gradient(ellipse 55% 55% at 12% 30%, ${T.accent}10, transparent 70%),` +
            `radial-gradient(ellipse 55% 55% at 88% 80%, ${T.warm}12, transparent 70%)`,
        }}
      />
      {/* Faint dotted texture */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: `radial-gradient(circle at center, ${T.text} 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          maskImage: "linear-gradient(to bottom, transparent, black 18%, black 82%, transparent)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
        {/* Header strip */}
        <div className="flex items-center gap-4 font-plexmono text-[10.5px] tracking-[0.32em] uppercase" style={{ color: T.muted }}>
          <span style={{ color: T.accent }}>Field</span>
          <span className="flex-1 h-px" style={{ background: T.border }} />
          <span className="inline-flex items-center gap-2"><EMark /> · CAP · FLD</span>
        </div>

        {/* Title + body */}
        <div className="mt-12 md:mt-16 max-w-3xl">
          <h2
            className="font-fraunces font-light text-4xl md:text-5xl lg:text-[60px] leading-[1.04] tracking-[-0.005em]"
            style={{ color: T.text, fontVariationSettings: "'SOFT' 50" }}
            data-testid="capability-field-headline"
          >
            Capability <span className="italic" style={{ color: T.accent }}>Field</span>
            <span style={{ color: T.warm }}>.</span>
          </h2>
          <p
            className="mt-6 max-w-xl font-intertight text-[15.5px] leading-[1.95]"
            style={{ color: T.textSoft }}
            data-testid="capability-field-body"
          >
            A connected view of how Erenown supports launch, operation, institutional systems, market access,
            and digital evolution through one coherent structure.
          </p>
        </div>

        {/* Flow visual */}
        <div className="mt-16 md:mt-20" data-testid="capability-field-flow">
          <svg
            viewBox={`0 0 ${VB_W} ${VB_H}`}
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-auto"
          >
            <defs>
              <linearGradient id="cf-flow" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor={T.accent} stopOpacity="0.85" />
                <stop offset="50%"  stopColor={T.text}   stopOpacity="0.5"  />
                <stop offset="100%" stopColor={T.warm}   stopOpacity="0.85" />
              </linearGradient>
              <radialGradient id="cf-node-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor={T.accent} stopOpacity="0.85" />
                <stop offset="60%" stopColor={T.accent} stopOpacity="0.2" />
                <stop offset="100%" stopColor={T.accent} stopOpacity="0" />
              </radialGradient>
              <radialGradient id="cf-warm-glow" cx="50%" cy="50%" r="50%">
                <stop offset="0%"  stopColor={T.warm} stopOpacity="0.95" />
                <stop offset="60%" stopColor={T.warm} stopOpacity="0.25" />
                <stop offset="100%" stopColor={T.warm} stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Drifting contour layers (background) */}
            <g className="constellation-drift" opacity="0.3">
              <path d={contour(-46)} fill="none" stroke={T.borderSoft} strokeWidth="1" />
              <path d={contour(-18)} fill="none" stroke={T.borderSoft} strokeWidth="1" />
              <path d={contour( 18)} fill="none" stroke={T.borderSoft} strokeWidth="1" />
              <path d={contour( 46)} fill="none" stroke={T.borderSoft} strokeWidth="1" />
            </g>

            {/* Vertical hairlines through each node, very faint */}
            <g opacity="0.12">
              {nodes.map((n) => (
                <line key={`v${n.id}`} x1={n.x} x2={n.x} y1="0" y2={VB_H} stroke={T.text} />
              ))}
            </g>

            {/* The flow — drawn once on enter via the existing constellation-line keyframe */}
            <path
              d={flowPath}
              fill="none"
              stroke="url(#cf-flow)"
              strokeWidth="1.6"
              strokeLinecap="round"
              className="constellation-line"
              style={{ "--dash-len": 2400, "--draw-delay": "0.3s" }}
            />

            {/* Capability nodes */}
            {nodes.map((n, i) => {
              const isOn = i < active;
              const isWarm = i === nodes.length - 1; // last node is the warm endpoint
              const isStart = i === 0;
              return (
                <g key={n.id} data-testid={`capability-field-node-${n.id}`}>
                  <circle
                    cx={n.x} cy={n.y} r={isOn ? 28 : 18}
                    fill={isWarm ? "url(#cf-warm-glow)" : "url(#cf-node-glow)"}
                    style={{ transition: "r 600ms cubic-bezier(.2,.9,.2,1)" }}
                  />
                  <circle
                    cx={n.x} cy={n.y} r="11"
                    fill={T.surface2}
                    stroke={isWarm ? T.warm : isStart ? T.accent : isOn ? T.accent : T.borderSoft}
                    strokeWidth={isOn ? 1.5 : 1}
                    style={{
                      transition: "stroke 600ms ease, stroke-width 600ms ease, opacity 600ms ease",
                      opacity: isOn ? 1 : 0.65,
                    }}
                  />
                  <circle
                    cx={n.x} cy={n.y} r="3.5"
                    fill={isWarm ? T.warm : T.accent}
                    style={{
                      opacity: isOn ? 1 : 0.4,
                      transition: "opacity 600ms ease",
                    }}
                  />

                  {/* Stage index above */}
                  <text
                    x={n.x} y={n.y - 36}
                    textAnchor="middle"
                    fontFamily="IBM Plex Mono, monospace"
                    fontSize="10"
                    letterSpacing="3"
                    fill={T.muted}
                    style={{ textTransform: "uppercase" }}
                  >
                    0{i + 1}
                  </text>

                  {/* Stage label below */}
                  <text
                    x={n.x} y={n.y + 56}
                    textAnchor="middle"
                    fontFamily="Fraunces, serif"
                    fontSize="22"
                    fill={T.text}
                    style={{
                      opacity: isOn ? 1 : 0.55,
                      transition: "opacity 700ms ease",
                    }}
                  >
                    {n.name}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* CTA strip */}
        <div className="mt-12 md:mt-16 flex flex-wrap items-end justify-between gap-6">
          <div className="font-plexmono text-[10px] tracking-[0.3em] uppercase max-w-md" style={{ color: T.muted }}>
            One coherent structure · Five connected stages
          </div>
          <Link
            to="/contact"
            data-testid="capability-field-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 font-plexmono text-[11px] tracking-[0.3em] uppercase"
            style={{ background: T.warm, color: T.bg }}
          >
            Contact Erenown <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
